/* eslint-disable camelcase */
import React, { useState } from 'react';
import { useObserver } from 'mobx-react';

import { useApolloClient } from '@apollo/react-hooks';

import useUser from 'hooks/use-user';

import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import VerifyMxMember from 'src/payments/graphql/queries/verify-mx-member.gql';
import { formatMXEvents, useCreateBankAccount } from 'src/payments/utils';
import {
  ENROLLMENT_EVENTS,
  EVENT_ACTIONS,
  useDutchiePayAnalytics,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import useLogRocket from 'hooks/use-logrocket';
import { disconnectBankAccount } from 'src/payments/dutchie-pay/signup/views/mx-widget/disconnect-bank-account.gql';
import { ListBankAccounts } from 'src/payments/graphql/queries/list-bank-accounts.gql';
import { callNativeMethod } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import { ShowEnrollment } from 'src/payments/graphql/queries/show-enrollment.gql';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';

import { AGGREGATORS, EnrollmentSteps } from 'src/payments/constants';
import { MXWidget } from './mx-widget-modal';

const SELECTED_INSTITUTION = `mx/connect/selectedInstitution`;
const MEMBER_CONNECTED = 'mx/connect/memberConnected';
const PRIMARY_ACTION = 'mx/connect/connected/primaryAction';
const MEMBER_DELETED = 'mx/connect/memberDeleted';
const OAUTH_REQUESTED = 'mx/connect/oauthRequested';

export function MXWidgetWrapper(): JSX.Element {
  const [selectedFinancialInstitution, setSelectedFinantialInstitution] = useState('');
  const User = useUser();
  const DutchiePayEnrollment = useDutchiePayEnrollment();

  const apolloClient = useApolloClient();
  const showErnie = useErnie();
  const { createMxBankAccount } = useCreateBankAccount();
  const mxWidgetUrl = useObserver(() => DutchiePayEnrollment.mxWidgetUrl);
  const mxMemberGuid = useObserver(() => DutchiePayEnrollment.mxMemberGuid);
  const mxMfaUrl = useObserver(() => DutchiePayEnrollment.mxMfaUrl);
  const { track } = useLogRocket();
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();

  const handleSelectFinancialInstitution = (financialInstitution): void =>
    setSelectedFinantialInstitution(financialInstitution);

  const handleDisconnectBankAccount = async (metaData): Promise<void> => {
    const { member_guid: memberGuid, user_guid: userGuid } = metaData;
    try {
      const response = await apolloClient.mutate({
        mutation: disconnectBankAccount,
        variables: {
          mxAccountInfo: {
            userGuid,
            memberGuid,
          },
        },
        refetchQueries: [
          {
            query: ShowEnrollment,
          },
          {
            query: ListBankAccounts,
            variables: { aggregator: DutchiePayEnrollment.aggregator },
          },
        ],
        awaitRefetchQueries: true,
      });

      const enrollmentStatus = await apolloClient.query({
        query: ShowEnrollment,
      });

      DutchiePayEnrollment.enrollmentStatusReceived(enrollmentStatus.data?.enrollmentStatus);

      if (response.errors) {
        showErnie(`Something went wrong, please try again`, `danger`);
        return;
      }
    } catch (err) {
      showErnie(`Something went wrong, please try again`, `danger`);
      console.log(err);
    }
  };

  // Move to a util??
  const verifyMxMember = async (): Promise<void> => {
    const verifyMxMemberProps = {
      memberGuid: mxMemberGuid,
      entityId: User.id,
      entityType: `User`,
      isMobileApp: false,
    };

    try {
      const verifyMxMemberEndpoint = apolloClient.watchQuery({
        query: VerifyMxMember,
        variables: verifyMxMemberProps,
        fetchPolicy: `no-cache`,
        pollInterval: 500,
        notifyOnNetworkStatusChange: true,
      });

      verifyMxMemberEndpoint.subscribe({
        next: ({ data, errors }) => {
          if (errors) {
            showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
            return;
          }

          const { identificationComplete } = data.verifyMxMember;
          if (identificationComplete) {
            verifyMxMemberEndpoint.stopPolling();

            const { needsAccountSelection, needsMfa, url, accounts } = data.verifyMxMember;
            const accountGuid = accounts[0]?.accountGuid;
            DutchiePayEnrollment.mxMemberDetailsReceived({ needsAccountSelection, needsMfa, url, accounts });

            if (!accountGuid) {
              DutchiePayEnrollment.setCurrentStep(EnrollmentSteps.unsupportedAccount);
            }

            if (!needsAccountSelection && accountGuid) {
              createMxBankAccount(accountGuid);
            }
          }
        },
      });
    } catch (err) {
      console.log(err);
      dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
        step: EnrollmentSteps.connectBank,
        action: EVENT_ACTIONS.FAILED,
      });
      showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
    }
  };

  const onEvent = (type, metaData): void => {
    const mxEventName = formatMXEvents(type);
    if (mxEventName) {
      dutchiePayEventTracker(ENROLLMENT_EVENTS.CONNECT_BANK_STEP, { action: mxEventName, source: AGGREGATORS.MX });
    }

    // this action is captured after the user selects an institution
    if (type === SELECTED_INSTITUTION) {
      handleSelectFinancialInstitution(metaData.name);
    }

    if (type === MEMBER_CONNECTED) {
      // this action is captured after a bank has verified credentials
      DutchiePayEnrollment.mxMemberCredentialsVerified(metaData.member_guid);
    }

    if (type === MEMBER_DELETED) {
      // this action is captured after a bank has been deleted
      void handleDisconnectBankAccount(metaData);
    }

    if (type === OAUTH_REQUESTED) {
      void callNativeMethod(`onLaunchUrl`, { url: metaData.url });
    }

    // this action is captured when the user clicks 'continue' after a bank connection is complete
    if (type === PRIMARY_ACTION) {
      DutchiePayEnrollment.mxBankConnectionCompleted();
      track(`DutchiePay_selected_financial_institution`, {
        financialInstitution: selectedFinancialInstitution,
      });
      void verifyMxMember();
    }
  };

  return <MXWidget mxWidgetUrl={mxMfaUrl || mxWidgetUrl} onEvent={onEvent} />;
}
